import { ChangeDetectorRef, Component, HostBinding, Input, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AutoComplete } from 'primeng/autocomplete';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { muAddressData } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import { AddressGeologicType } from '../../../models/interface/addressGeologicType.enum';
import { AddressSuggestedShared } from '../../../models/interface/addressSuggested.interface';
import { CityShared } from '../../../models/interface/cityShared.interface';
import { MapAddressShared } from '../../../models/interface/mapAddress.interface';
import { FavoriteAddressShared } from '../../../models/interface/favoriteAddress.interface';
import { ItemNumberToFilterShared } from '../../../models/interface/itemNumberToFilter.interface';
import { StorageClassifier } from '../../../models/interface/storageClassifier.enum';
import { MuFavoriteService } from '../../../sharedProtos/favorite/mu-favorite.service';
import { MuGeologicService } from '../../../sharedProtos/geologic/mu-geologic.service';
import { StorageService } from '../../../sharedServices/storage.service';
import { Actions } from '../../../types/actions.type';
import { MuException } from '../../../types/exception.type';
import { Flags } from '../../../types/flags.type';
import { MuComponent } from '../../../types/MuComponent.componetent';
import { MuFormAddressFlag } from './mu-form-address.flag';
import { ValidatorService } from '../../validator/validator.service';
import { UtilsService } from '../../../sharedServices/utils.service';
import { AddressDataAction } from '../../../models/interface/addressDataaction.interface';

@Component({
  selector: 'app-mu-form-address',
  templateUrl: './mu-form-address.component.html',
  styleUrls: ['./mu-form-address.component.sass'],
})
export class MuFormAddressComponent<FlagType, ActionType> extends MuComponent {
  @Input() formData: UntypedFormGroup;
  @Input() controlName: string;
  @Input() searchBy = 'address';
  @Input() placeholder: string;
  @Input() flagList: Flags<FlagType>;
  @Input() loadingFlag: FlagType;
  @Input() countryIso: string;
  @Input() userId: number;
  @Input() cityControlName: string;
  @Input() instructionsControlName: string;
  @Input() city: CityShared;
  @Input() actionList: Actions<ActionType, any>;
  @Input() buildCurrentAction: ActionType;
  @Input() favoriteList: AddressSuggestedShared[] = [];
  @Input() createFavoritePermission: boolean;
  @Input() updateAddressAction: ActionType;
  @Input() deleteFieldAddressAction: ActionType;
  @Input() showDeleteFieldAddress: boolean;
  @Input() hideTooltip: boolean;
  @Input() cityListBase: CityShared[];
  @Input() cityList: ItemNumberToFilterShared[];
  @Input() editableMask: boolean;
  @ViewChild('pac') componentClass: AutoComplete;
  @HostBinding('class') baseClass = 'mu-form-address';

  delay: number = muAddressData.addressDelay;
  addressType = AddressGeologicType;
  loadingList: AddressSuggestedShared[] = [];
  suggestedList: AddressSuggestedShared[] = [];
  tmpSuggestedList: AddressSuggestedShared[] = [];
  currentAddress: AddressSuggestedShared[] = [];
  selfFlagList: Flags<MuFormAddressFlag> = new Flags<MuFormAddressFlag>();
  customFormData: UntypedFormGroup;
  customControlName = 'value';
  error: boolean;

  constructor(
    private muGeologicService: MuGeologicService,
    private translateService: TranslateService,
    public formBuilder: UntypedFormBuilder,
    private storageService: StorageService,
    private muFavoriteService: MuFavoriteService,
    private validatorService: ValidatorService,
    private changeDetectorRef: ChangeDetectorRef,
    private utilsService: UtilsService
  ) {
    super();

    this.loadingList = [
      {
        type: AddressGeologicType.LOADING,
      },
      {
        type: AddressGeologicType.LOADING,
      },
    ];

    this.customFormData = this.formBuilder.group({
      value: '',
    });
  }

  async muOnInit(): Promise<void> {
    this.createCustomDate();
    this.watchFields();
    this.buildCurrentAddress();
  }

  createCustomDate() {
    this.customFormData
      .get(this.customControlName)
      .setValue({ address: this.formData.get(this.controlName).value || '' }, { emitEvent: false });
    this.changeDetectorRef.detectChanges();
    if (this.formData.get(this.controlName).disabled) {
      this.customFormData.get(this.customControlName).disable({ onlySelf: true });
    }
  }

  muOnDestroy(): void {
    /** */
  }

  /**
   * build current address
   */
  buildCurrentAddress(): void {
    const tmp = this.storageService.getValue(
      StorageClassifier.PERSISTENT_FILTER,
      muAddressData.addressCurrentKey
    );
    if (!tmp) {
      return environment.guardClause;
    }
    const value = JSON.parse(tmp) as MapAddressShared;
    if (!value?.address || value?.address?.trim()?.length <= 0) {
      this.storageService.removeValue(
        StorageClassifier.PERSISTENT_FILTER,
        muAddressData.addressCurrentKey
      );
      return environment.guardClause;
    }
    this.currentAddress = [
      {
        type: AddressGeologicType.CURRENT,
        address: value.address,
        text: this.translateService.instant('common.currentLocation'),
        iconClass: this.muGeologicService.getAddressIcon(AddressGeologicType.CURRENT),
        cityId: value.cityId,
      },
    ];
  }

  /**
   * watch fields
   */
  watchFields(): void {
    this.actionList?.on(this.buildCurrentAction, (_, __) => {
      this.buildCurrentAddress();
    });
    if (this.cityListBase && this.cityControlName) {
      this.watch(this.formData.get(this.cityControlName).valueChanges).subscribe(() => {
        this.setCurrentCity();
      });
    }
    this.watch(this.selfFlagList.valueChanges(MuFormAddressFlag.LOADING)).subscribe(flag => {
      this.setSuggestedFlagList(flag.value);
      this.changeDetectorRef.detectChanges();
    });
    this.watch(this.flagList?.valueChanges(this.loadingFlag)).subscribe(flag => {
      this.setSuggestedFlagList(flag.value);
      this.changeDetectorRef.detectChanges();
    });
    this.watch(this.customFormData.get(this.customControlName).valueChanges).subscribe(() => {
      this.setSuggestedPreview();
      try {
        if (!this.componentClass || typeof this.componentClass.show !== 'function') {
          return;
        }

        if (
          !this.componentClass.value.address &&
          (typeof this.componentClass.value !== 'string' ||
            this.componentClass.value === '[object Object]')
        ) {
          Object.assign(this.componentClass.value, { address: '' });
          this.formData.controls[this.controlName].setValue('');
          this.changeDetectorRef.detectChanges();
        }
        this.componentClass.show();
        this.changeDetectorRef.detectChanges();
      } catch (e: any) {
        /** */
      }
    });
    this.watch(this.customFormData.get(this.customControlName).valueChanges)
      .pipe(distinctUntilChanged(), debounceTime(muAddressData.addressDelay))
      .subscribe(value => {
        this.updateAddress(value);
        this.getSuggestedList(value);
      });
    this.watch(this.formData.get(this.controlName).valueChanges)
      .pipe(distinctUntilChanged())
      .subscribe(address => {
        this.customFormData
          .get(this.customControlName)
          .setValue({ address: address?.address || address }, { emitEvent: false });
      });
    this.watch(this.formData.get(this.controlName).statusChanges)
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        this.error = value + '' === 'INVALID';
      });
  }

  /**
   * set current city
   */
  setCurrentCity(): void {
    const value = this.formData?.get(this.cityControlName)?.value;
    this.city = this.cityListBase?.find(x => x?.id === parseInt(value + '', 10));
  }

  /**
   * set suggested flag list
   * @param {boolean} flagValue
   */
  setSuggestedFlagList(flagValue: boolean): void {
    if (!flagValue && !this.flagList?.get(this.loadingFlag)) {
      this.suggestedList = this.tmpSuggestedList;
    }
    if (this.suggestedList.length === 0) {
      try {
        if (!this.componentClass || typeof this.componentClass.hide !== 'function') {
          return;
        }
        this.componentClass?.hide();
        setTimeout(() => {
          this.changeDetectorRef.detectChanges();
        });
      } catch (e: any) {
        /** */
      }
    }
  }

  /**
   * set suggested list preview
   */
  setSuggestedPreview(wihtOutLoading?: boolean): void {
    this.suggestedList = [...this.currentAddress, ...this.favoriteList];

    if (!wihtOutLoading) {
      this.suggestedList = [...this.suggestedList, ...this.loadingList];
    }
  }

  /**
   * get suggested list
   */
  async getSuggestedList(address: FavoriteAddressShared): Promise<void> {
    if (!this.isValidForm(address.address || address.toString())) {
      return environment.guardClause;
    }
    this.updateAddress(address);
    try {
      this.selfFlagList.enable(MuFormAddressFlag.LOADING);
      this.flagList.enable(this.loadingFlag, null, false);
      this.setSuggestedPreview();
      this.tmpSuggestedList = await this.muGeologicService.getSuggestedList({
        countryIso: this.countryIso,
        userId: this.userId,
        city: this.city,
        address: address.address || address.toString(),
      });
      this.tmpSuggestedList =
        this.tmpSuggestedList.length === 0
          ? [...this.currentAddress, ...this.tmpSuggestedList, ...this.favoriteList]
          : this.tmpSuggestedList;
    } catch (e: any | MuException) { /* empty */ } finally {
      this.selfFlagList.disable(MuFormAddressFlag.LOADING);
    }
  }

  /**
   * show panel if have favorites or current
   * @param {Autocomplete} element
   * @param {boolean} wihtOutLoading
   */
  showPanel(element: AutoComplete, wihtOutLoading?: boolean): void {
    this.setSuggestedPreview(wihtOutLoading);

    if (this.suggestedList?.length > 0) {
      try {
        element?.show();
      } catch (e: any) {
        /** */
      }
    }
  }

  /**
   * check form
   * @param {string} address
   * @returns {boolean} response
   */
  isValidForm(address: string): boolean {
    return (
      this.countryIso?.length > 0 &&
      this.userId > 0 &&
      this.city?.geoName.length > 0 &&
      address?.length > 0
    );
  }

  /**
   * on select event
   * @param {FavoriteAddressShared} address
   */
  updateAddress(
    address: FavoriteAddressShared | AddressSuggestedShared,
    isFromSelect?: boolean
  ): void {
    if ((address as AddressSuggestedShared)?.type === AddressGeologicType.LOADING) {
      this.formData.controls[this.controlName].setValue('');
      return environment.guardClause;
    }
    if (this.updateAddressAction && isFromSelect) {
      this.actionList?.emit(this.updateAddressAction, {
        cityId: address.cityId,
        cityGeoName: address.city,
        cityControlName: this.cityControlName,
        instructionsControlName: this.instructionsControlName,
        instructions: address.addressComplement,
        isInCityList: this.isInCityList(address.cityId, address.cityName || address.city),
      });
    }
    this.formData.get(this.controlName).setValue(address?.address || address, { emitEvent: true });
  }

  /**
   *
   * @param address
   */
  updateAddresEventAutoComplete(address: AddressDataAction) {
    this.updateAddress(address.value, true);
  }

  /**
   * create favorite address
   */
  async createFavoriteAddress(): Promise<void> {
    if (
      this.isAddressFavorite ||
      !this.currentAddressValue ||
      this.formData?.get(this.controlName)?.errors ||
      this.loadingFlag ||
      this.selfFlagList?.get(MuFormAddressFlag.LOADING) ||
      !this.createFavoritePermission
    ) {
      return environment.guardClause;
    }
    const newAddress: AddressSuggestedShared = {
      address: this.currentAddressValue,
      type: AddressGeologicType.FAVORITE_OTHER,
    };
    this.setCurrentCity();
    if (!this.city?.geoName) {
      return environment.guardClause;
    }

    const date = new Date();
    const dateObject = this.utilsService.getDateAndTime(date.toString());
    const alias = 'other__' + dateObject.startDate + '_' + dateObject.startTime;

    try {
      await this.muFavoriteService.createFavorite({
        ...newAddress,
        cityId: this.city?.id,
        country: this.countryIso,
        userId: this.userId,
        addressType: 'other',
        alias: alias,
        addressComplement: '',
      });
      this.favoriteList.push(newAddress);
// eslint-disable-next-line no-empty
    } catch (e: any | MuException) {}
  }

  /**
   * is tooltip disabled
   * @returns {boolean} response
   */
  get isAddressFavorite(): boolean {
    return (
      this.favoriteList?.findIndex(
        x => x.address?.toLowerCase()?.trim() === this.currentAddressValue?.toLowerCase()?.trim()
      ) > -1
    );
  }

  /**
   * get current address value in form group
   * @returns {string} response
   */
  get currentAddressValue(): string {
    return this.formData?.get(this.controlName).value?.toString();
  }

  /**
   * is selected address in city list
   * @param {number} id
   * @param {string} geoName
   * @returns {boolean} response
   */
  isInCityList(id: number, geoName: string): boolean {
    return (
      this.cityList?.findIndex(
        x => x.value === id || x.extra?.toLowerCase()?.trim() === geoName?.toLowerCase()?.trim()
      ) > -1
    );
  }

  /**
   * check form control
   */
  checkControl(): void {
    this.validatorService.validateControl(this.formData, this.controlName, this.changeDetectorRef);
  }

  /**
   * delete field address
   */
  deleteFieldAddress(): void {
    this.actionList.emit(this.deleteFieldAddressAction, this.controlName);
  }
}
